import {
  CrFormStep,
  getInitialStepsState,
  PER_WITNESS_STEPS,
  witnessStepKey,
} from '@/components/Event/CrForm/CrFormStep'
import {
  crReadWaiveToRecord,
  DEFAULT_WITNESS_CR_FIELDS,
} from '@/components/Event/CrForm/saFields'
import { AttendeeRole, TurnaroundHours } from '@/constants'
import {
  AttendeeFormValues,
  CrFormStepsState,
  Event,
  EventFeedback,
  ReporterFeedback,
} from '@/types'

type WitnessEvent = Pick<Event, 'is_transcript_order_required'>
type ErrataEvent = Pick<Event, 'errata_policy'>

export const getSelectableWitnessSectionRoles = ({
  roles,
  attendees,
  attendeeIndex,
}: {
  roles: AttendeeRole[]
  attendees: AttendeeFormValues[]
  attendeeIndex: number
}): AttendeeRole[] => {
  const currentRole = attendees[attendeeIndex]?.role
  const hasWitness = attendees.some(
    attendee => attendee.role === AttendeeRole.WITNESS,
  )

  return hasWitness && currentRole !== AttendeeRole.WITNESS
    ? roles.filter(role => role !== AttendeeRole.WITNESS)
    : roles
}

export const canRemoveAttendeeFromWitnessSection = ({
  attendeeRole,
}: {
  attendeeRole: AttendeeRole | null | undefined
}): boolean => attendeeRole !== AttendeeRole.WITNESS

const createWitnessFeedback = (
  event: WitnessEvent,
  attendees: AttendeeFormValues[],
): EventFeedback => ({
  result: 'completed',
  witness_requested_transcript: false,
  transcript_ordered: event.is_transcript_order_required,
  turnaround_hours: event.is_transcript_order_required
    ? TurnaroundHours.SEVEN_DAYS
    : TurnaroundHours.ROUGH_DRAFT,
  cna_type: null,
  cna_type_other_text: null,
  cna_declared_at: null,
  id_type: null,
  id_number: null,
  id_screenshots: [],
  timestamp_log: null,
  additional_notes: null,
  transcript_orders: null,
  ...DEFAULT_WITNESS_CR_FIELDS,
  attendees,
})

const resetAttendanceStep = (steps: CrFormStepsState): CrFormStepsState => ({
  ...steps,
  [CrFormStep.ATTENDANCE]: { expanded: true, done: false },
})

export const canAddWitnessFeedback = (
  values: ReporterFeedback,
  activeWitnessIndex: number,
): boolean =>
  values.events_feedbacks[activeWitnessIndex]?.attendees.filter(
    attendee => attendee.role === AttendeeRole.WITNESS,
  ).length === 1

export const appendWitnessFeedback = ({
  values,
  event,
  activeWitnessIndex,
  witnessId,
  details,
}: {
  values: ReporterFeedback
  event: WitnessEvent
  activeWitnessIndex: number
  witnessId: string
  // Already-known details for the new witness. Omitted when added from the Attendance
  // step, which focuses the empty name input instead;
  // Identity, role and mapping are always set here, whatever `details` carries.
  details?: Partial<AttendeeFormValues>
}): ReporterFeedback => {
  if (!canAddWitnessFeedback(values, activeWitnessIndex)) {
    return values
  }

  const newWitness: AttendeeFormValues = {
    name: '',
    email: null,
    present: null,
    ...details,
    id: witnessId,
    role: AttendeeRole.OTHER,
    zoom_participants: [],
  }
  const feedbacksWithNewAttendee = values.events_feedbacks.map(
    (feedback, index) =>
      index < activeWitnessIndex
        ? feedback
        : {
            ...feedback,
            attendees: [
              ...feedback.attendees,
              { ...newWitness, zoom_participants: [] },
            ],
          },
  )
  const sourceAttendees =
    feedbacksWithNewAttendee[feedbacksWithNewAttendee.length - 1]?.attendees ??
    []
  const newWitnessAttendees = sourceAttendees.map(attendee => ({
    ...attendee,
    role:
      attendee.id === witnessId
        ? AttendeeRole.WITNESS
        : attendee.role === AttendeeRole.WITNESS
          ? AttendeeRole.OTHER
          : attendee.role,
    zoom_participants: [...(attendee.zoom_participants ?? [])],
  }))
  const events_feedbacks = [
    ...feedbacksWithNewAttendee,
    createWitnessFeedback(event, newWitnessAttendees),
  ]

  return {
    ...values,
    events_feedbacks,
    steps: resetAttendanceStep(
      getInitialStepsState(events_feedbacks.length, values.steps),
    ),
  }
}

const hasWitnessStepProgress = (
  steps: CrFormStepsState | undefined,
  witnessIndex: number,
): boolean =>
  PER_WITNESS_STEPS.some(
    step => steps?.[witnessStepKey(step, witnessIndex)]?.done === true,
  )

// Read/waive is compared to what an untouched section holds: the Closing step fills in
// 'na' on its own where the event's rule means the witness is not asked.
const hasWitnessFeedbackProgress = (
  feedback: EventFeedback,
  event: ErrataEvent,
): boolean =>
  feedback.result !== 'completed' ||
  feedback.witness_requested_transcript ||
  feedback.cna_declared_at !== null ||
  feedback.cna_type !== null ||
  (feedback.cna_type_other_text ?? '') !== '' ||
  feedback.id_type !== null ||
  (feedback.id_number ?? '') !== '' ||
  feedback.id_screenshots.length > 0 ||
  (feedback.timestamp_log ?? '') !== '' ||
  (feedback.additional_notes ?? '') !== '' ||
  feedback.transcript_orders !== null ||
  (feedback.spellings ?? '') !== '' ||
  (feedback.read_waive ?? null) !==
    crReadWaiveToRecord(event.errata_policy, null) ||
  feedback.orders_present !== null ||
  feedback.on_record === true ||
  Object.keys(feedback.attendee_orders ?? {}).length > 0

export const canRemoveWitnessFeedback = (
  values: ReporterFeedback,
  witnessIndex: number,
  event: ErrataEvent,
): boolean => {
  const feedback = values.events_feedbacks[witnessIndex]

  return (
    values.events_feedbacks.length > 1 &&
    witnessIndex > 0 &&
    feedback !== undefined &&
    feedback.attendees.some(
      attendee => attendee.role === AttendeeRole.WITNESS,
    ) &&
    !hasWitnessStepProgress(values.steps, witnessIndex) &&
    !hasWitnessFeedbackProgress(feedback, event)
  )
}

const removeAndReindexWitnessSteps = (
  steps: CrFormStepsState | undefined,
  removedWitnessIndex: number,
): CrFormStepsState => {
  const perWitnessStepNames = new Set<string>(PER_WITNESS_STEPS)

  return Object.entries(steps ?? {}).reduce<CrFormStepsState>(
    (nextSteps, [key, value]) => {
      const [step, rawWitnessIndex] = key.split(':')
      const witnessIndex = Number(rawWitnessIndex)

      if (
        rawWitnessIndex === undefined ||
        !perWitnessStepNames.has(step) ||
        Number.isNaN(witnessIndex)
      ) {
        nextSteps[key] = value
        return nextSteps
      }
      if (witnessIndex === removedWitnessIndex) {
        return nextSteps
      }

      const nextWitnessIndex =
        witnessIndex > removedWitnessIndex ? witnessIndex - 1 : witnessIndex
      nextSteps[witnessStepKey(step as CrFormStep, nextWitnessIndex)] = value
      return nextSteps
    },
    {},
  )
}

export const removeWitnessFeedback = (
  values: ReporterFeedback,
  witnessIndex: number,
  event: ErrataEvent,
): ReporterFeedback => {
  if (!canRemoveWitnessFeedback(values, witnessIndex, event)) {
    return values
  }

  const removedWitnessId = values.events_feedbacks[witnessIndex].attendees.find(
    attendee => attendee.role === AttendeeRole.WITNESS,
  )?.id
  // An attendee is repeated across witness sections. Remove the deleted witness from
  // every remaining copy as well, otherwise an empty OTHER attendee left in an earlier
  // section would keep Attendance invalid after the witness section is removed.
  const events_feedbacks = values.events_feedbacks.flatMap(
    (feedback, index) => {
      if (index === witnessIndex) {
        return []
      }
      if (removedWitnessId === undefined) {
        return [feedback]
      }
      // A remaining section's own witness can share the id: a draft reloads with one id
      // per attendee name, so a witness added twice under the same name gets one id.
      // Keep that witness and their order; only the copies attending as others go.
      if (
        feedback.attendees.some(
          attendee =>
            attendee.id === removedWitnessId &&
            attendee.role === AttendeeRole.WITNESS,
        )
      ) {
        return [
          {
            ...feedback,
            attendees: feedback.attendees.filter(
              attendee =>
                attendee.id !== removedWitnessId ||
                attendee.role === AttendeeRole.WITNESS,
            ),
          },
        ]
      }

      const { [removedWitnessId]: _removedOrder, ...attendee_orders } =
        feedback.attendee_orders ?? {}
      return [
        {
          ...feedback,
          attendee_orders,
          attendees: feedback.attendees.filter(
            attendee => attendee.id !== removedWitnessId,
          ),
        },
      ]
    },
  )
  const reindexedSteps = removeAndReindexWitnessSteps(
    values.steps,
    witnessIndex,
  )

  return {
    ...values,
    events_feedbacks,
    steps: resetAttendanceStep(
      getInitialStepsState(events_feedbacks.length, reindexedSteps),
    ),
  }
}
